import React, { useState } from "react";
import { Copy } from "react-feather";
import { toast } from "react-toastify";

// Shared Components
import SearchDropdown from "../../input/SearchDropdown";
import LoadingSpinner from "../../LoadingSpinner";
import Button from "../../input/Button";

// APIs & Constants
import { getOtherRefSearchDropdown } from "../../../api/keyvalue";
import { copyRate } from "../../../api/finance/rateManagerApi";

const RateCopyModal = ({
                           customerSupplier,
                           rateType,
                           rahHeaderId,
                           closePopup,
                           setUpdateNeeded,
                           user,
                       }) => {
    const [customer, setCustomer] = useState("");
    const [shipper, setShipper] = useState("");
    const [isCopying, setIsCopying] = useState(false);
    const [error, setError] = useState("");

    const targetRoleName = rateType === 2 ? "Transporter" : "Customer";
    const targetShipperRoleName = rateType === 2 ? "Requesting Customer" : "Shipper";

    const createRateCopy = async (e) => {
        if (e) e.preventDefault();

        if (!customer) {
            setError(`Please select a ${targetRoleName}`);
            toast.error(`Please select a ${targetRoleName}`, {
                containerId: "Modal",
                autoClose: 5000,
            });
            return;
        }

        if (!rahHeaderId) {
            toast.error("Rate Master identifier not found", {
                containerId: "Modal",
                autoClose: 5000,
            });
            return;
        }

        try {
            setIsCopying(true);
            const copyResponse = await copyRate({
                rahId: rahHeaderId,
                customerId: customer,
                shipperId: shipper,
            });

            if (copyResponse?.status === 200) {
                toast.success(
                    typeof copyResponse?.data === "string"
                        ? `${copyResponse?.data} created successfully!`
                        : "Rate copied successfully!",
                    {
                        containerId: "Modal",
                        autoClose: 5000,
                    }
                );

                if (typeof setUpdateNeeded === "function") {
                    setUpdateNeeded(true);
                }
                closePopup();
            } else {
                toast.error(`${copyResponse?.data || "Unable to copy rate"}`, {
                    containerId: "Modal",
                    autoClose: 5000,
                });
            }
        } catch (err) {
            const errorMessage =
                err?.response?.data?.message ||
                err?.response?.data ||
                err?.message ||
                "Failed to copy rate";
            toast.error(
                typeof errorMessage === "string" ? errorMessage : JSON.stringify(errorMessage),
                {
                    containerId: "Modal",
                    autoClose: 5000,
                }
            );
        } finally {
            setIsCopying(false);
        }
    };

    return (
        <div style={{ padding: "10px" }}>
            {/* Modal Title Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    paddingBottom: "12px",
                    marginBottom: "16px",
                    borderBottom: "1px solid #e0e0e0",
                }}
            >
                <div
                    style={{
                        backgroundColor: "#e8f4f8",
                        borderRadius: "8px",
                        padding: "8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Copy size={20} color="#2185d0" />
                </div>
                <div>
                    <h3 style={{ margin: 0, fontSize: "1.15rem", color: "#2c3e50" }}>
                        Clone Rate Master
                    </h3>
                    <span style={{ fontSize: "0.85rem", color: "#7f8c8d" }}>
                        Duplicate configuration to a new target stakeholder
                    </span>
                </div>
            </div>

            {/* Target Form Card */}
            <div
                style={{
                    backgroundColor: "#fff",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "20px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                }}
            >
                <form className="ui form" onSubmit={(e) => e.preventDefault()}>
                    <div className="field">
                        <SearchDropdown
                            label={`Target ${targetRoleName}`}
                            required
                            onResultSelect={(data) => {
                                setCustomer(data);
                                setError("");
                            }}
                            api={getOtherRefSearchDropdown}
                            apiDependency={{
                                type: "stakeholder_type",
                                value: rateType === 2 ? "Transporter" : "Billing Customer",
                            }}
                            fieldInfo={{
                                tableName: "masterdata.tms_m_stakeholder",
                                primaryKey: "stk_id",
                                fieldName: "stk_name",
                                id: customer,
                            }}
                            error={error}
                            resetError={() => setError("")}
                            disabled={!rateType}
                        />
                    </div>

                    <div className="field" style={{ marginTop: "15px" }}>
                        <SearchDropdown
                            label={`Target ${targetShipperRoleName}`}
                            onResultSelect={(data) => setShipper(data)}
                            api={getOtherRefSearchDropdown}
                            apiDependency={
                                rateType === 2
                                    ? {
                                        type: "user_stakeholder",
                                        value: "Requesting Customer",
                                        param2: user?.username || "",
                                    }
                                    : {
                                        type: "stakeholder_type",
                                        value: "shipper_by_billing_customer",
                                        param2: customerSupplier ? customerSupplier.toString() : "",
                                    }
                            }
                            fieldInfo={{
                                tableName: "masterdata.tms_m_stakeholder",
                                primaryKey: "stk_id",
                                fieldName: "stk_name",
                                id: shipper,
                            }}
                            disabled={!rateType}
                        />
                    </div>

                    {/* Action Buttons Footer */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            gap: "10px",
                            marginTop: "20px",
                            paddingTop: "15px",
                            borderTop: "1px solid #eee",
                        }}
                    >
                        <Button
                            type="button"
                            className="ui primary button"
                            onClick={createRateCopy}
                            disabled={isCopying}
                            isLoading={isCopying}
                            Icon={Copy}
                            label="Copy Rate Master"
                        />
                    </div>
                </form>
            </div>

            {/* Modal Processing Overlay */}
            {isCopying && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        height: "100%",
                        width: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.4)",
                        zIndex: 9999,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <LoadingSpinner message="Cloning Rate Master..." />
                </div>
            )}
        </div>
    );
};

export default RateCopyModal;